import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { take, tap } from 'rxjs';
import { ApplicationTemplateService } from '../application-template/application-template.service';
import { Router } from '@angular/router';
import { RouteConstants } from '../../../../../core/constants/routes';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ModalComponent } from '../../../../../shared/components/modal/modal.component';
import { hasOpenDialogs } from '../../../../../core/functions/dialog-helper';
import { ApplicationDeletionDialogMessage } from './constants/application-deletion-dialog-message';
import { ApplicationService } from '../application/application.service';
import { ReturnRouteService } from '../../../../../core/services/navigation/return-route.service';

@Injectable({
    providedIn: 'root',
})
export class ApplicationDeletionService {
    constructor(
        private readonly router: Router,
        private readonly dialog: MatDialog,
        private readonly alertService: AlertService,
        private readonly applicationService: ApplicationService,
        private readonly applicationTemplateService: ApplicationTemplateService,
        private readonly returnRouteService: ReturnRouteService,
    ) {}

    /**
     * Handles the deletion of an application.
     * Displays a confirmation dialog and deletes the application if confirmed.
     * @param applicationId - The ID of the application to delete.
     */
    deleteApplication(applicationId: string): void {
        this.displayApplicationDeletionConfirmationDialog(applicationId);
    }

    /**
     * Handles the deletion of an application template.
     * Displays a confirmation dialog and deletes the template if confirmed.
     * @param applicationTemplateId - The ID of the application template to delete.
     */
    deleteApplicationTemplate(applicationTemplateId: number): void {
        this.displayTemplateDeletionConfirmationDialog(applicationTemplateId);
    }

    /**
     * Displays a confirmation dialog for deleting an application.
     * If the user confirms, it deletes the application.
     */
    private displayApplicationDeletionConfirmationDialog(
        applicationId: string,
    ): void {
        if (hasOpenDialogs(this.dialog)) {
            return;
        }

        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: ApplicationDeletionDialogMessage.application.modalTitle,
                content:
                    ApplicationDeletionDialogMessage.application.modalContent,
                primaryAction: {
                    title: ApplicationDeletionDialogMessage.application
                        .primaryActionTitle,
                    classList: '!bg-error-700 !text-tertiary-50',
                    action: () => this.handleApplicationDeletion(applicationId),
                },
            },
        });
    }

    /**
     * Displays a confirmation dialog for deleting an application template.
     * If the user confirms, it deletes the template.
     */
    private displayTemplateDeletionConfirmationDialog(
        applicationTemplateId: number,
    ): void {
        if (hasOpenDialogs(this.dialog)) {
            return;
        }

        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: ApplicationDeletionDialogMessage.template.modalTitle,
                content: ApplicationDeletionDialogMessage.template.modalContent,
                primaryAction: {
                    title: ApplicationDeletionDialogMessage.template
                        .primaryActionTitle,
                    classList: '!bg-error-700 !text-tertiary-50',
                    action: () =>
                        this.handleTemplateDeletion(applicationTemplateId),
                },
            },
        });
    }

    /**
     * Handles the deletion of the application.
     * After deletion, it navigates to the application root URL and displays a success alert.
     * @param applicationId - The ID of the application to delete.
     */
    private handleApplicationDeletion(applicationId: string): void {
        this.applicationService
            .delete(applicationId)
            .pipe(
                take(1),
                tap(() => {
                    const returnRoute = this.returnRouteService.consume();
                    if (returnRoute) {
                        this.router.navigate([returnRoute]);
                    } else {
                        this.router.navigate([
                            RouteConstants.Dashboard.getRootUrl(),
                        ]);
                    }

                    this.alertService.displaySuccessAlert(
                        ApplicationDeletionDialogMessage.application
                            .successfulAlertMessage,
                        false,
                        { durationInMs: 5000 },
                    );
                }),
            )
            .subscribe();
    }

    /**
     * Handles the deletion of the application template.
     * After deletion, it navigates to the template root URL and displays a success alert.
     * @param applicationTemplateId - The ID of the application template to delete.
     */
    private handleTemplateDeletion(applicationTemplateId: number): void {
        this.applicationTemplateService
            .delete(applicationTemplateId)
            .pipe(
                take(1),
                tap(() => {
                    this.router.navigate([
                        RouteConstants.Template.getRootUrl(),
                    ]);

                    this.alertService.displaySuccessAlert(
                        ApplicationDeletionDialogMessage.template
                            .successfulAlertMessage,
                        false,
                        { durationInMs: 5000 },
                    );
                }),
            )
            .subscribe();
    }
}
